/**
 * The Cortex Console Overview (harness row CC1B-13; spec.md 8A ViewOverview,
 * requirements.md FR-14, design.md section 11 "rundown first, then attention counts,
 * then fleet").
 *
 * What this component does: render the answer `GET /api/cortex/console/overview`
 * gave - the rundown the store already held, the four counts the server derived from
 * the same columns the Agents board shows, and the environments the caller may see.
 *
 * What it deliberately does NOT do:
 *
 * - ask for a rundown. The Agentic Operator writes rundowns on its own timer; there
 *   is no control here that could start one (requirements.md FR-9, corpus row
 *   UAT-OVW-1 forbidden fallback `ao_synthesis_on_read`);
 * - decide whether a rundown is current. `stale` is the server's verdict against its
 *   own configured window, so nothing here is compared against a clock;
 * - invent a number. An answer that never reached a store carries no counts, and this
 *   blade shows none rather than four zeroes, which would read as "nothing needs
 *   attention";
 * - decide who may see a rundown, a decision or an environment. Eligibility was
 *   applied once by the read layer.
 */
import React from "react";
import { ContentRegionLoader } from "../components/loading";
import type {
  ConsoleAgentsScope,
  ConsoleFleetEntry,
  ConsoleOverviewResponse,
  ConsoleRefusal,
  ConsoleRundown,
} from "../api/types";
import { idLabel } from "../labels/displayLabels";
import styles from "./OverviewBlade.module.css";

/**
 * The words a person reads for each count the server names. The server owns the count
 * SET and its order; this map only supplies English for the keys it sends, and a key
 * with no entry is still shown, titled from the key itself through the display-label
 * mapping - so a count Cortex starts reporting can never go missing from this page.
 */
const COUNT_LABELS: Record<string, string> = {
  pending_decisions: "Decisions waiting",
  stalled_sessions: "Sessions blocked",
  silent_sessions: "Sessions gone silent",
  ready_for_review: "Ready for review",
};

export function countLabel(key: string): string {
  return idLabel(key, COUNT_LABELS);
}

/** A value the server did not report, said in words rather than left blank. */
export function reported(value: string | null | undefined): string {
  return value ? value : "not reported";
}

export function isOverview(
  body: ConsoleOverviewResponse | ConsoleRefusal,
): body is ConsoleOverviewResponse {
  return (body as ConsoleOverviewResponse).response_type === "console_overview";
}

export type OverviewBladeProps = {
  overview: ConsoleOverviewResponse | ConsoleRefusal | null;
  loading: boolean;
  onRefresh: () => void;
};

function Rundown({ rundown }: { rundown: ConsoleRundown }) {
  return (
    <div data-testid="overview-rundown">
      <div className={styles.rundownHead}>
        <h3 className={styles.sectionTitle}>Agentic Operator rundown</h3>
        {rundown.stale ? (
          <span className={styles.stale} data-testid="overview-rundown-stale">
            Older than Cortex treats as current
          </span>
        ) : null}
      </div>
      <p className={styles.line} data-testid="overview-rundown-when">
        Written: {reported(rundown.generated_at)}
        {rundown.review_date ? ` (for ${rundown.review_date})` : ""}
      </p>
      <p className={styles.summary} data-testid="overview-rundown-summary">
        {reported(rundown.summary)}
      </p>
      {rundown.attention_items && rundown.attention_items.length > 0 ? (
        <div data-testid="overview-rundown-attention">
          <h4 className={styles.subTitle}>What it flagged</h4>
          <ul className={styles.list}>
            {rundown.attention_items.map((item) => (
              <li key={item} className={styles.line}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {rundown.next_actions && rundown.next_actions.length > 0 ? (
        <div data-testid="overview-rundown-next">
          <h4 className={styles.subTitle}>What it suggested next</h4>
          <ul className={styles.list}>
            {rundown.next_actions.map((item) => (
              <li key={item} className={styles.line}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className={styles.provenance}>
        Stream: {reported(rundown.stream)} - marking: {reported(rundown.classification)}
      </p>
    </div>
  );
}

function FleetRow({ entry }: { entry: ConsoleFleetEntry }) {
  const name = entry.environment_name || entry.environment;
  return (
    <article
      className={styles.fleetEntry}
      data-testid={`overview-fleet-${name || "unnamed"}`}
    >
      <h4 className={styles.fleetName}>{reported(name)}</h4>
      <p className={styles.line}>Drift: {reported(entry.drift_state)}</p>
      <p className={styles.line}>Running: {reported(entry.code_version)}</p>
      <p className={styles.line}>Database at: {reported(entry.db_applied_order)}</p>
      <p className={styles.line}>
        Last deployed: {reported(entry.last_deployed_at)} by{" "}
        {reported(entry.last_deployed_by)}
      </p>
    </article>
  );
}

export function OverviewBlade({ overview, loading, onRefresh }: OverviewBladeProps) {
  const answered = overview && isOverview(overview) ? overview : null;
  const message = overview?.message;
  const counts = answered?.attention_counts;
  const fleet = answered?.fleet;

  return (
    <div className={styles.blade} data-testid="console-overview-blade">
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Overview</h2>
          {answered?.scope_statement ? (
            <p className={styles.scopeStatement} data-testid="overview-scope-statement">
              {answered.scope_statement}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.refresh}
          data-testid="overview-refresh"
          onClick={onRefresh}
        >
          Refresh
        </button>
      </div>

      {message ? (
        <p className={styles.message} data-testid="overview-message">
          {message}
        </p>
      ) : null}

      {loading ? (
        <ContentRegionLoader
          label="Loading the Overview"
          data-testid="overview-loading"
        />
      ) : null}

      {!loading && answered ? (
        <>
          <section className={styles.section}>
            {answered.rundown_absent === false && answered.rundown ? (
              <Rundown rundown={answered.rundown} />
            ) : answered.rundown_absent === true ? (
              <div data-testid="overview-rundown-absent">
                <h3 className={styles.sectionTitle}>Agentic Operator rundown</h3>
                <p className={styles.line}>
                  There is no rundown in Cortex yet. The Agentic Operator writes one on
                  its own timer - opening this page does not ask for one.
                </p>
              </div>
            ) : null}
          </section>

          {counts ? (
            <section className={styles.section} data-testid="overview-attention">
              <h3 className={styles.sectionTitle}>What needs attention</h3>
              <div className={styles.counts}>
                {Object.entries(counts).map(([key, value]) => (
                  <div
                    key={key}
                    className={styles.count}
                    data-testid={`overview-count-${key}`}
                  >
                    <span className={styles.countValue}>{value}</span>
                    <span className={styles.countLabel}>{countLabel(key)}</span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {fleet ? (
            <section className={styles.section} data-testid="overview-fleet">
              <h3 className={styles.sectionTitle}>Fleet</h3>
              {fleet.length === 0 ? (
                <p className={styles.line}>No environments are visible to you.</p>
              ) : (
                <div className={styles.fleetGrid}>
                  {fleet.map((entry) => (
                    <FleetRow
                      key={entry.environment_name || entry.environment}
                      entry={entry}
                    />
                  ))}
                </div>
              )}
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
